import {
    useCallback, useMemo,
} from 'react'
import AddIcon from '@mui/icons-material/Add'
import Typography from '@mui/material/Typography'

import RichTable from 'app/shared-components/RichTableReduxWrapper'
import Card from 'app/shared-components/Card'
import useLabel from 'app/hooks/useLabel'
import useHasPermission from 'app/hooks/useHasPermission'
import useEnumValues from 'app/hooks/useEnumValues'
import {
    ProductType,
} from 'app/enums/label.enums'
import {
    generateSkyNetLink,
} from 'app/SkyNetRoutes'
import useAccountManagementRoutes from 'app/SkyNetRoutes/hooks/useAccountManagementRoutes'
import useTableStyles from 'app/tss/table.styles'
import useLinkStyles from 'app/tss/link.styles'

import useRequestTable from 'app/hooks/useRequestTable'
import {
    AccountLanesTable,
} from 'app/Apps/AccountManagement/AccountLanes/AccountLanes.types'
import {
    RequestKeys,
} from 'app/hooks/useRequest'
import requests from 'app/requests/Lane/Lane.requests'
import {
    RequestedLane,
} from '../../AQuotes.types'
import getColumns from './RequestedLanes.columns'

export default function RequestedLanes({
    requestedLanes,
    aQuoteId,
}: Readonly<{
    requestedLanes: RequestedLane[],
    aQuoteId: number,
}>) {
    const {
        classes,
    } = useTableStyles()
    const {
        classes: linkClasses,
    } = useLinkStyles()
    const {
        AQuotes: AQuotesRoutes,
        Lanes: LanesRoutes,
    } = useAccountManagementRoutes()
    const l = useLabel()
    const getProductTypesAllowedValues = useEnumValues()(ProductType)
    const requestedLaneIds = useMemo(() => {
        return requestedLanes.map(({
            accountLane,
        }) => {
            return accountLane?.id
        })
    }, [requestedLanes])

    const {
        isDataReady,
        data: {
            data: lanes,
        } = {},
    } = useRequestTable<{ data: AccountLanesTable[] }>({
        key: RequestKeys.filterRequestAllAccount,
        params: {},
        defaultValue: {
            filter: {
                id: requestedLaneIds,
            },
            sort: {
                field: 'laneNumber',
                direction: 'asc',
            },
            rows: 200,
        },
        config: requests.filterRequestAllAccount,
        keepPreviousData: true,
    })

    const columns = useMemo(() => {
        return getColumns({
            l,
            laneStyle: linkClasses.tableLink,
            getLaneLink: (id) => {
                return id ? generateSkyNetLink({
                    domainPath: LanesRoutes.Overview.path,
                    params: {
                        key: id,
                    },
                }) : null
            },
        })
    }, [
        LanesRoutes.Overview.path,
        l,
        linkClasses.tableLink,
    ])

    const accountUpdate = useHasPermission(['account_update_all'])

    const load = useCallback(() => {
        if (isDataReady) {
            const lanesTableData = lanes.reduce((acc, lane) => {
                const requestedLane = requestedLanes.find((el) => {
                    return el.accountLane?.id === lane.id
                })

                if (!requestedLane) {
                    return acc
                }

                return [
                    ...acc,
                    {
                        ...requestedLane,
                        productTypes: requestedLane.productTypes?.map((productType) => {
                            return getProductTypesAllowedValues[productType]
                        }),
                        ...lane,
                    },
                ]
            }, [])

            return Promise.resolve({
                data: lanesTableData,
            })
        }
        return Promise.resolve({
            data: [],
        })
    }, [
        isDataReady,
        lanes,
        requestedLanes,
        getProductTypesAllowedValues,
    ])

    const moveToUpdateTab = () => {
        AQuotesRoutes.Update.navigate({
            params: {
                key: aQuoteId,
            },
            options: {
                state: {
                    path: 'overview',
                },
            },
        })
    }

    return (
        <Card
            className={classes.cardWrapper}
        >
            <div className={classes.headerWrapper}>
                <Typography variant="h3">
                    Requested Lanes
                </Typography>
                {accountUpdate && (
                    <div
                        className={classes.actionWrapper}
                        data-testid="btn-add-lane"
                    >
                        <AddIcon />
                        <a
                            onKeyDown={moveToUpdateTab}
                            onClick={moveToUpdateTab}
                        >
                            Add Existing Lane
                        </a>
                    </div>
                )}
            </div>
            <RichTable
                configName="RequestedLanesTable"
                name="Requested Lanes"
                load={load}
                columns={columns}
                uniqField="id"
                className={classes.tableContent}
                classNames={{
                    contentWrapper: classes.tableContentWrapper,
                }}
            />
        </Card>
    )
}
